// src/store/ordersSlice.js
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { config } from "../config";
import { getVendorId, getSessionToken } from "../utils/authSession";

const API_BASE_URL = config.API_MWSSTOREFRONT_BASE_URL;

// Helper function to map API data to component's format
const mapOrderData = (data) => {
	return data.map((order) => ({
		id: order.orderId,
		orderNumber: order.orderNumber,
		customerName: order.buyerName || 'N/A',
		dateOfOrder: order.orderDate,
		numberOfItems: order.itemCount,
		totalAmount: order.totalValue,
		orderStatus: order.orderStatus,
		fulfillmentType: order.fulfillmentType,
		customerTableId: order.customerTableId,
		realCustomerId: order.realCustomerId,
		orderCustomerId: order.orderCustomerId,
		storeName: order.storeName,
	}));
};

// Cached data is only reusable by the vendor that fetched it.
const isCacheValid = (state, vendorId, cachedData) => {
	if (!cachedData) return false;
	if (state.orders.ownerVendorId !== vendorId) return false;
	return dayjs().diff(dayjs(cachedData.timestamp)) < 120000;
};

// Async thunk to fetch orders by date with caching
export const fetchOrdersByDate = createAsyncThunk(
	'orders/fetchOrdersByDate',
	async (
		{
			date,                    // string in 'YYYY-MM-DD'
			forceRefresh = false,
			vendorId: vId,           // optional override; if omitted we'll read from storage
			sessionToken: sTok,      // optional override
		},
		{getState, rejectWithValue}
	) => {
		try {
			const vendorId = vId ?? getVendorId();
			const sessionToken = sTok ?? getSessionToken();

			if (!vendorId) throw new Error('vendorId missing');
			if (!sessionToken) throw new Error('sessionToken missing');

			const state = getState();
			const cachedData = state.orders.ordersByDate[date];

			if (!forceRefresh && isCacheValid(state, vendorId, cachedData)) {
				return {date, vendorId, orders: cachedData.orders, fromCache: true};
			}

			const url =
				`${API_BASE_URL}/storefront/orm/${encodeURIComponent(vendorId)}` +
				`/orders/byDate?date=${encodeURIComponent(date)}`;

			const resp = await fetch(url, {
				headers: {'X-Session-Token': sessionToken},
			});

			const payload = await resp.json().catch(() => null);
			if (!resp.ok) {
				const msg =
					(payload && (payload.message || payload.error)) ||
					`Failed to fetch orders (${resp.status})`;
				throw new Error(msg);
			}

			const mappedOrders = mapOrderData(Array.isArray(payload) ? payload : []);
			return {date, vendorId, orders: mappedOrders, fromCache: false};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch orders');
		}
	}
);

const initialState = {
	ownerVendorId: null, // vendor the cached data below belongs to
	ordersByDate: {}, // { 'YYYY-MM-DD': { orders: [...], timestamp: number } }
	orderSummariesByWeek: {}, // { 'YYYY-MM-DD': { summaries: [...], timestamp: number } }
	loadingOrders: false,
	loadingSummaries: false,
	errorOrders: null,
	errorSummaries: null,
};

const claimOwner = (state, vendorId) => {
	if (state.ownerVendorId !== vendorId) {
		state.ordersByDate = {};
		state.orderSummariesByWeek = {};
		state.ownerVendorId = vendorId;
	}
};

const ordersSlice = createSlice({
	name: 'orders',
	initialState,
	reducers: {
		resetOrders() {
			return initialState;
		},
	},
	extraReducers: (builder) => {
		// Handle fetchOrdersByDate
		builder
			.addCase(fetchOrdersByDate.pending, (state) => {
				state.loadingOrders = true;
				state.errorOrders = null;
			})
			.addCase(fetchOrdersByDate.fulfilled, (state, action) => {
				const {date, vendorId, orders, fromCache} = action.payload;
				if (!fromCache) {
					claimOwner(state, vendorId);
					state.ordersByDate[date] = {
						orders,
						timestamp: dayjs().valueOf(),
					};
				}
				state.loadingOrders = false;
			})
			.addCase(fetchOrdersByDate.rejected, (state, action) => {
				state.loadingOrders = false;
				state.errorOrders = action.payload || 'Failed to fetch orders';
				console.error('Error fetching orders:', action.error);
			});
	},
});

export const {resetOrders} = ordersSlice.actions;

// Components read ordersByDate directly; hide data that belongs to another vendor.
const EMPTY = Object.freeze({});
export const selectOrdersByDate = (state) =>
	state.orders.ownerVendorId === getVendorId() ? state.orders.ordersByDate : EMPTY;

export default ordersSlice.reducer;
